import { useState } from 'react';
import {
  AudioButton,
  Button,
  ChoiceButton,
  ExerciseCard,
  FlipCard,
  ResultBanner,
  SubmitBar,
  TapChip,
  TextInput,
  ThemeToggle,
  type ResultState,
} from '@fluent/ui';
import './Cluster3Review.css';

/**
 * Cluster 3 — Lesson primitives.
 * ExerciseCard / ChoiceButton / TapChip / ResultBanner / SubmitBar / AudioButton.
 * Shown in isolation and in a full "Translate" exercise end-to-end.
 */
export function Cluster3Review() {
  // Multiple-choice mock state
  const CHOICES = [
    { id: 'a', text: 'გამარჯობა, როგორ ხარ?' },
    { id: 'b', text: 'ნახვამდის, კარგად იყავი.' },
    { id: 'c', text: 'მე ვგრძნობ თავს კარგად.' },
  ];
  const CORRECT = 'a';
  const [picked, setPicked] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState>('neutral');

  const submit = () => {
    if (!picked) return;
    setResult(picked === CORRECT ? 'correct' : 'wrong');
  };
  const reset = () => {
    setPicked(null);
    setResult('neutral');
  };

  // Tap-to-build state
  const POOL = ['learning', 'We', 'are', 'English', 'every', 'day'];
  const [answer, setAnswer] = useState<number[]>([]);
  const addToken = (i: number) => {
    if (answer.includes(i)) return;
    setAnswer((a) => [...a, i]);
  };
  const removeToken = (i: number) => setAnswer((a) => a.filter((x) => x !== i));
  const clearAnswer = () => setAnswer([]);

  // FlipCard demo
  const [flipped, setFlipped] = useState(false);

  // Audio button
  const [playing, setPlaying] = useState(false);
  const playAudio = () => {
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), 1800);
  };

  return (
    <main className="c3">
      <header className="c3__hero">
        <p className="c3__kicker">Phase 1 · Cluster 3</p>
        <h1>Lesson primitives</h1>
        <p className="c3__lede">
          The pieces every exercise is built from. Card frame, choice button, tap chips, audio, submit / result.
        </p>
        <div className="c3__theme-row">
          <ThemeToggle />
        </div>
      </header>

      {/* 1. Full multiple-choice exercise flow */}
      <section className="c3__section">
        <h2>Pick-the-translation flow</h2>
        <p className="c3__note">End-to-end: tap a choice, hit Check, see result. Reset to try again.</p>

        <ExerciseCard kicker="აირჩიე თარგმანი" prompt="“Hello, how are you?”">
          {CHOICES.map((c, i) => {
            const isPicked = picked === c.id;
            const revealed = result !== 'neutral';
            const isCorrect = c.id === CORRECT;
            const state = !revealed
              ? 'idle'
              : isCorrect
              ? 'correct'
              : isPicked
              ? 'wrong'
              : 'idle';
            return (
              <ChoiceButton
                key={c.id}
                selected={isPicked}
                state={state}
                bullet={String.fromCharCode(65 + i)}
                onClick={() => !revealed && setPicked(c.id)}
                disabled={revealed}
              >
                {c.text}
              </ChoiceButton>
            );
          })}
        </ExerciseCard>

        <div className="c3__cta-dock">
          {result === 'neutral' ? (
            <SubmitBar
              ctaLabel="შემოწმება"
              ctaDisabled={!picked}
              onCta={submit}
              secondaryLabel="გამოტოვება"
              onSecondary={reset}
            />
          ) : (
            <ResultBanner
              state={result}
              title={result === 'correct' ? 'სწორია!' : 'არასწორია'}
              detail={
                result === 'wrong'
                  ? `სწორი პასუხია: ${CHOICES.find((c) => c.id === CORRECT)!.text}`
                  : 'საუცხოოა! გააგრძელე.'
              }
              onCta={reset}
            />
          )}
        </div>
      </section>

      {/* 2. Tap-to-build flow */}
      <section className="c3__section">
        <h2>Tap-to-build sentence</h2>
        <p className="c3__note">
          Tap tokens from the pool into the answer row. Tap a chip in the answer row to return it to the pool.
        </p>

        <ExerciseCard
          kicker="ააწყე წინადადება"
          prompt="ჩვენ ვსწავლობთ ინგლისურს ყოველდღე."
          subprompt={'Target: "We are learning English every day"'}
        >
          <div className="fluent-tapchip-answer">
            {answer.map((idx) => (
              <TapChip key={idx} onClick={() => removeToken(idx)}>
                {POOL[idx]}
              </TapChip>
            ))}
          </div>
          <div className="fluent-tapchip-pool">
            {POOL.map((word, i) => (
              <TapChip key={i} hidden={answer.includes(i)} onClick={() => addToken(i)}>
                {word}
              </TapChip>
            ))}
          </div>
        </ExerciseCard>

        <div className="c3__controls">
          <Button variant="ghost" size="sm" onClick={clearAnswer}>
            გასუფთავება
          </Button>
        </div>
      </section>

      {/* 3. Listen-and-type with AudioButton */}
      <section className="c3__section">
        <h2>Listen-and-type</h2>
        <p className="c3__note">AudioButton plays; pulse halo shows while &ldquo;playing&rdquo;. No real audio wired here.</p>

        <ExerciseCard kicker="მოუსმინე და აკრიფე">
          <div className="c3__audio-row">
            <AudioButton size="lg" playing={playing} onClick={playAudio} />
            <p className="c3__audio-hint">
              {playing ? 'უკრავს…' : 'დააჭირე მოსასმენად'}
            </p>
          </div>
          <TextInput label="დაწერე ის, რაც გესმის" placeholder="…" />
        </ExerciseCard>
      </section>

      {/* 4. Primitive gallery — sizes + states */}
      <section className="c3__section">
        <h2>Gallery</h2>
        <p className="c3__note">Each primitive in isolation with its key states + sizes.</p>

        <div className="c3__gallery">
          <div>
            <p className="c3__label">ChoiceButton — states</p>
            <div className="c3__stack">
              <ChoiceButton bullet="A">Default</ChoiceButton>
              <ChoiceButton bullet="B" selected>Selected</ChoiceButton>
              <ChoiceButton bullet="C" state="correct">Correct</ChoiceButton>
              <ChoiceButton bullet="D" state="wrong">Wrong</ChoiceButton>
              <ChoiceButton bullet="E" disabled>Disabled</ChoiceButton>
            </div>
          </div>

          <div>
            <p className="c3__label">TapChip — states</p>
            <div className="c3__row">
              <TapChip>default</TapChip>
              <TapChip hidden>hidden</TapChip>
              <TapChip answerState="correct">correct</TapChip>
              <TapChip answerState="wrong">wrong</TapChip>
              <TapChip disabled>disabled</TapChip>
            </div>
          </div>

          <div>
            <p className="c3__label">AudioButton — sizes</p>
            <div className="c3__row c3__row--centered">
              <AudioButton size="sm" />
              <AudioButton size="md" />
              <AudioButton size="lg" />
              <AudioButton size="md" playing />
              <AudioButton size="md" disabled />
            </div>
          </div>

          <div>
            <p className="c3__label">FlipCard — tap to flip (one-way, flashcards)</p>
            <FlipCard
              flipped={flipped}
              onFlip={() => setFlipped(true)}
              flipLabel="გადაატრიალე ბარათი"
              front={<span style={{ fontSize: 24, fontWeight: 800 }}>წყალი</span>}
              back={<span style={{ fontSize: 24, fontWeight: 800 }}>water</span>}
            />
            <div className="c3__row c3__row--centered" style={{ marginTop: 12 }}>
              <Button variant="neutral" size="sm" onClick={() => setFlipped(false)}>
                თავიდან
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="c3__footer">
        <p>
          <b>Cluster 3 review.</b> Tell Jake: exercise card spacing, choice-button selected feel, tap-chip press
          physics, audio pulse speed, banner slide-in.
        </p>
      </footer>
    </main>
  );
}
